import { Injectable } from '@angular/core';
import { defer, Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, take, timeout } from 'rxjs/operators';
import { WebCustomer } from 'src/Model/WebCustomer';
import { ApiService } from '../service/api.service';

/**
 * `TemplateData.NewDesign` — the one switch between the legacy and the
 * redesigned Customer Portal. Missing / false (the default) keeps every
 * legacy component; true swaps each legacy component that has a redesigned
 * twin for that twin:
 *
 *   pages (redirected by DesignRouteGuard, see design-twins.ts):
 *     /menu → /new-menu, /app-menu → /app-new-menu, /dine-in[/:tableId] →
 *     /new-dine-in[/:tableId], /checkout → /new-checkout, /guest →
 *     /new-guest-checkout, /reservation → /new-reservation, /products → /shop,
 *     /join-reward → /rewards, /my-rewards → /rewards?view=my-rewards,
 *     /giftcard → /gift-cards
 *   shell:
 *     app-header → app-new-header, login → auth, recomendations →
 *     new-recomendations, the legacy extras popup → recipe-option-modal
 *     (ProductCardService), app-search's legacy look → the .ui-search look
 *
 * It replaced `Header.New`, `Login.New`, `Recommendations.New` and
 * `Checkout.New` (Oct 2026). Pages without a twin (online, branches, store,
 * category-products, likeAndShare, reserve-iframe …) render as they are in
 * both modes. Nothing redirects the other way: with the switch off a brand
 * that links to a redesigned page still gets it.
 *
 * Read the switch here only — never `TemplateData.NewDesign` directly.
 */
@Injectable({ providedIn: 'root' })
export class DesignService {
  /**
   * The webCustomer payload once it has arrived. Waits at most
   * CONFIG_WAIT_MS: when the request fails the store stays null, and the
   * route guard must not hold a page back for ever (it falls back to legacy).
   * Built per subscription, so a timed-out wait doesn't stick: the next
   * navigation sees the config once it has landed.
   */
  readonly webCustomer$: Observable<WebCustomer | null>;

  /** true / false once the brand config is known (replays the last value). */
  readonly isNew$: Observable<boolean>;

  /** Snapshot for synchronous call sites; false until the config arrives. */
  isNew = false;

  /** TemplateData.CheckoutDetail.DistributorLogin — these brands stay on the legacy checkout (new-checkout has no distributor handling). */
  distributorLogin = false;

  /** TemplateData.CheckoutDetail.simpleCheckout — still wins over both checkouts until order-checkout is retired. */
  simpleCheckout = false;

  constructor(private apiService: ApiService) {
    this.webCustomer$ = defer(() =>
      this.apiService.getWebcustomer$.pipe(
        filter((res) => !!res),
        take(1),
        timeout(CONFIG_WAIT_MS),
        catchError(() => of(null))
      )
    );

    this.isNew$ = this.apiService.getWebcustomer$.pipe(
      filter((res) => !!res),
      map((res) => isNewDesign(res)),
      shareReplay(1)
    );

    this.apiService.getWebcustomer$.subscribe((res) => {
      if (!res) return;
      this.isNew = isNewDesign(res);
      this.distributorLogin = !!res.TemplateData?.CheckoutDetail?.DistributorLogin;
      this.simpleCheckout = !!res.TemplateData?.CheckoutDetail?.simpleCheckout;
    });
  }

  /**
   * The checkout page a "go to checkout" should open (no brand prefix):
   * distributor brands → legacy `checkout`; simpleCheckout → `order-checkout`;
   * otherwise `new-checkout` when the switch is on, legacy `checkout` when off.
   * Pass `{ simpleCheckout: false }` where the simple checkout doesn't apply
   * (the store page), and `simpleBeforeDistributor: true` from the
   * recommendation popups, which always sent simpleCheckout brands to
   * order-checkout, distributors included.
   */
  checkoutPage(opts?: {
    simpleCheckout?: boolean;
    simpleBeforeDistributor?: boolean;
  }): string {
    const simple = (opts?.simpleCheckout ?? true) && this.simpleCheckout;
    // the recommendation popups always let simpleCheckout win (as before)
    if (opts?.simpleBeforeDistributor && simple) return 'order-checkout';
    if (this.distributorLogin) return 'checkout';
    if (simple) return 'order-checkout';
    return this.isNew ? 'new-checkout' : 'checkout';
  }
}

/** How long the route guard waits for the brand config before giving up. */
export const CONFIG_WAIT_MS = 10000;

/** `TemplateData.NewDesign === true`; anything else is the legacy design. */
export function isNewDesign(res: WebCustomer | null | undefined): boolean {
  return res?.TemplateData?.NewDesign === true;
}
